import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CButton, CCard, CCardBody, CCol, CImage, CRow, CSpinner, CBadge } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faClose, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { freelancerUser } from "@/constants/translation/freelancerUser.translation";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import { freelancerUser as freelancerUserRoutes } from "@/libs/routes";
import { useGetFreelancerUser, useApproveService, useRejectService } from "@/hooks/useFreelancerUser";
import constants from "@/libs/constant";

const isTrue = (value) => value === true || value === constants.status.apiStatus.true;

const getServiceStatusBadgeClass = (status) => {
  switch (status) {
    case "approved":
      return "success";
    case "rejected":
      return "danger";
    default:
      return "warning";
  }
};

const ViewFreelancerService = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const { id, serviceId } = useParams();

  const {
    data: response,
    isLoading,
    error: freelancerUserError,
    refetch,
  } = useGetFreelancerUser(id, null, (error) => {
    console.error("Error fetching freelancer user details:", error);
  });

  const freelancerUserData = response?.data;
  const service = useMemo(
    () => freelancerUserData?.services?.find((item) => item.uuid === serviceId),
    [freelancerUserData, serviceId],
  );

  const { mutate: doApproveService } = useApproveService(() => {
    toast.success(t(freelancerUser.view.services.messages.approve_success));
    refetch();
  });
  const { mutate: doRejectService } = useRejectService(() => {
    toast.success(t(freelancerUser.view.services.messages.reject_success));
    refetch();
  });

  const handleBack = () => navigate(freelancerUserRoutes.view.replace(":id", id));

  const handleApproveService = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(freelancerUser.view.services.approve_popup.heading)}</h2>
            <p className="text-center mb-3">{t(freelancerUser.view.services.approve_popup.message)}</p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doApproveService(service.uuid);
                  onClose();
                }}
              >
                {t(freelancerUser.view.services.approve_popup.yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(freelancerUser.view.services.approve_popup.no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const handleRejectService = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(freelancerUser.view.services.reject_popup.heading)}</h2>
            <p className="text-center mb-3">{t(freelancerUser.view.services.reject_popup.message)}</p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doRejectService(service.uuid);
                  onClose();
                }}
              >
                {t(freelancerUser.view.services.reject_popup.yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(freelancerUser.view.services.reject_popup.no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <CSpinner color="primary" />
      </div>
    );
  }

  if (freelancerUserError || !freelancerUserData || !service) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <h4>{t(freelancerUser.serviceDetail.error.not_found)}</h4>
          <p>{t(freelancerUser.serviceDetail.error.not_found_description)}</p>
          <CButton color="primary" onClick={handleBack}>
            {t(freelancerUser.serviceDetail.back_btn)}
          </CButton>
        </div>
      </div>
    );
  }

  const na = t(freelancerUser.serviceDetail.defaults.na);

  return (
    <div className="view-freelancer-service-container">
      <CCard className="mb-4 profile-header-card">
        <CCardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0 d-flex align-items-center gap-2">{service.serviceName || na}</h2>
            <CButton
              size="sm"
              style={{ backgroundColor: "#212631", borderColor: "#212631", color: "white" }}
              onClick={handleBack}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              {t(freelancerUser.serviceDetail.back_btn)}
            </CButton>
          </div>
          <div className="d-flex gap-2">
            {service.status !== "approved" && (
              <CButton color="success" onClick={handleApproveService}>
                <FontAwesomeIcon icon={faCheck} className="me-2" />
                {t(freelancerUser.view.services.actions.approve)}
              </CButton>
            )}
            {service.status !== "rejected" && (
              <CButton color="danger" onClick={handleRejectService}>
                <FontAwesomeIcon icon={faXmark} className="me-2" />
                {t(freelancerUser.view.services.actions.reject)}
              </CButton>
            )}
          </div>
        </CCardBody>
      </CCard>

      <CCard className="mb-4">
        <CCardBody>
          <h4 className="card-title mb-4">{t(freelancerUser.serviceDetail.details_title)}</h4>
          <CRow>
            <CCol md={6} className="mb-3">
              <strong>{t(freelancerUser.serviceDetail.service_name_label)}:</strong>
              <p className="text-muted">{service.serviceName || na}</p>
            </CCol>
            <CCol md={6} className="mb-3">
              <strong>{t(freelancerUser.serviceDetail.category_label)}:</strong>
              <p className="text-muted">{service.serviceCategory?.name || na}</p>
            </CCol>
            <CCol md={6} className="mb-3">
              <strong>{t(freelancerUser.serviceDetail.price_label)}:</strong>
              <p className="text-muted">{service.price}</p>
            </CCol>
            <CCol md={6} className="mb-3">
              <strong>{t(freelancerUser.serviceDetail.duration_label)}:</strong>
              <p className="text-muted">
                {service.durationValue} {service.durationUnit}
              </p>
            </CCol>
            <CCol md={6} className="mb-3">
              <strong>{t(freelancerUser.serviceDetail.negotiable_label)}:</strong>
              <p className="text-muted">
                {isTrue(service.isNegotiable)
                  ? t(freelancerUser.serviceDetail.negotiable_yes)
                  : t(freelancerUser.serviceDetail.negotiable_no)}
              </p>
            </CCol>
            <CCol md={6} className="mb-3">
              <strong>{t(freelancerUser.serviceDetail.status_label)}:</strong>{" "}
              <p className="text-muted">
                <CBadge
                  color={getServiceStatusBadgeClass(service.status)}
                  className="text-capitalize"
                  style={{ fontSize: "0.75rem", padding: "0.35em 0.55em" }}
                >
                  {service.status}
                </CBadge>
              </p>
            </CCol>
            <CCol md={12} className="mb-3">
              <strong>{t(freelancerUser.serviceDetail.description_label)}:</strong>
              <p className="text-muted">{service.description || na}</p>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <CCard className="mb-4">
        <CCardBody>
          <h4 className="card-title mb-4">{t(freelancerUser.serviceDetail.addresses.title)}</h4>
          {service.addresses?.length ? (
            service.addresses.map((address) => (
              <CCard key={address.uuid} className="mb-3">
                <CCardBody>
                  <CRow>
                    <CCol md={4} className="mb-2">
                      <strong>{t(freelancerUser.serviceDetail.addresses.location_type_label)}:</strong>{" "}
                      <span className="text-muted">{address.locationType || na}</span>
                    </CCol>
                    <CCol md={4} className="mb-2">
                      <strong>{t(freelancerUser.serviceDetail.addresses.house_number_label)}:</strong>{" "}
                      <span className="text-muted">{address.houseNumber || na}</span>
                    </CCol>
                    <CCol md={4} className="mb-2">
                      <strong>{t(freelancerUser.serviceDetail.addresses.street_label)}:</strong>{" "}
                      <span className="text-muted">{address.street || na}</span>
                    </CCol>
                    <CCol md={4} className="mb-2">
                      <strong>{t(freelancerUser.serviceDetail.addresses.city_label)}:</strong>{" "}
                      <span className="text-muted">{address.city || na}</span>
                    </CCol>
                    <CCol md={4} className="mb-2">
                      <strong>{t(freelancerUser.serviceDetail.addresses.region_label)}:</strong>{" "}
                      <span className="text-muted">{address.region || na}</span>
                    </CCol>
                    <CCol md={4} className="mb-2">
                      <strong>{t(freelancerUser.serviceDetail.addresses.zip_code_label)}:</strong>{" "}
                      <span className="text-muted">{address.zipCode || na}</span>
                    </CCol>
                    <CCol md={4} className="mb-2">
                      <strong>{t(freelancerUser.serviceDetail.addresses.country_label)}:</strong>{" "}
                      <span className="text-muted">{address.country || na}</span>
                    </CCol>
                    <CCol md={8} className="mb-2">
                      <strong>{t(freelancerUser.serviceDetail.addresses.coordinates_label)}:</strong>{" "}
                      <span className="text-muted">
                        {address.latitude && address.longitude ? `${address.latitude}, ${address.longitude}` : na}
                      </span>
                    </CCol>
                  </CRow>
                </CCardBody>
              </CCard>
            ))
          ) : (
            <p className="text-muted">{t(freelancerUser.serviceDetail.addresses.no_addresses)}</p>
          )}
        </CCardBody>
      </CCard>

      <CCard className="mb-4">
        <CCardBody>
          <h4 className="card-title mb-4">{t(freelancerUser.serviceDetail.images.title)}</h4>
          {service.images?.length ? (
            <div className="d-flex flex-wrap gap-3">
              {service.images.map((image) => (
                <a key={image.uuid} href={image.imageUrl} target="_blank" rel="noreferrer">
                  <CImage
                    src={image.imageUrl}
                    alt="service"
                    style={{ width: "150px", height: "150px", objectFit: "cover", borderRadius: "6px" }}
                  />
                </a>
              ))}
            </div>
          ) : (
            <p className="text-muted">{t(freelancerUser.serviceDetail.images.no_images)}</p>
          )}
        </CCardBody>
      </CCard>
    </div>
  );
};

ViewFreelancerService.breadCrumbArray = [
  {
    name: breadcrumb_key.freelancerUser?.list,
    pathname: freelancerUserRoutes.list,
    active: false,
  },
  {
    name: breadcrumb_key.freelancerUser?.view,
    pathname: "",
    active: true,
  },
];

export default ViewFreelancerService;
